import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { apiClient } from "../utils/const";
import { toast } from "react-toastify";
import { useTheme } from "./ThemeContext";

const EditPaymentModal = ({ paymentId, onClose, onSuccess }) => {
  const [schemeType, setSchemeType] = useState(localStorage.getItem("schemeType")); // mandal or kanchan
  const [payment, setPayment] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const { dark } = useTheme();

  const normalizePaymentType = (value) => {
    if (!value) return "";
    const v = String(value).trim().toLowerCase();
    if (v === "cash") return "Cash";
    if (v === "upi") return "UPI";
    if (v === "cheque" || v === "bank transfer" || v === "banktransfer" || v === "bank_transfer") return "Bank Transfer";
    if (v === "net banking" || v === "netbanking") return "Net Banking";
    return value; // fallback to original
  };

  // Listen for scheme changes
  useEffect(() => {
    const handleSchemeChange = (event) => {
      const newScheme = event.detail.to;
      setSchemeType(newScheme);
    };

    window.addEventListener("schemeChanged", handleSchemeChange);
    return () => window.removeEventListener("schemeChanged", handleSchemeChange);
  }, []);

  // Load payment details
  useEffect(() => {
    const fetchData = async () => {
      try {
        const paymentsEndpoint =
          schemeType === "kanchan"
            ? "/kanchan/fetch_kanchan_payments.php"
            : "/mandal/fetch_mandal_payments.php";

        const paymentsRes = await apiClient.post(paymentsEndpoint);

        const allPayments = paymentsRes.data?.data?.transactions;

        const foundPayment = allPayments.find(
          (p) => String(p.id) === String(paymentId)
        );

        if (!foundPayment) {
          alert("❌ Payment not found");
          onClose();
          return;
        }

        setPayment(foundPayment);
        setAmount(foundPayment.amount);
        setPaymentType(normalizePaymentType(foundPayment.transaction_method));
        setPaymentDate(foundPayment.transaction_date);
      } catch (error) {
        console.error("Error loading data", error);
        alert("❌ Failed to load data");
        onClose();
      }
    };

    fetchData();
  }, [paymentId, onClose, schemeType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!payment) return;

    const endpoint =
      schemeType === "kanchan"
        ? "/kanchan/update_kanchan_payments.php"
        : "/mandal/update_mandal_payments.php";

    const payload = {
      payment_id: paymentId,
      amount: parseFloat(amount),
      payment_type: normalizePaymentType(paymentType),
      payment_date: paymentDate,
    };

    try {
      const res = await apiClient.post(endpoint, payload);

      if (res.data.message === "radha") {
        toast.success("Payment updated successfully!");
        onSuccess?.();
        onClose();
      } else {
        toast.error("Failed to update payment");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error while updating");
    }
  };

  const getFullName = (user) =>
    [user.first_name, user.middle_name, user.last_name].filter(Boolean).join(" ");

  const selectStyles = useMemo(
    () => ({
      control: (provided) => ({
        ...provided,
        minHeight: 44,
        backgroundColor: dark ? "rgba(255,255,255,0.06)" : "#f8f9fa",
        borderColor: dark ? "rgba(255,255,255,0.1)" : "var(--bs-border-color)",
        boxShadow: "none",
        color: dark ? "#f1f1f1" : "#212529",
        borderRadius: 12,
        transition: "all 0.2s ease",
        opacity: 0.9,
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: "6px 12px",
      }),
      singleValue: (provided) => ({
        ...provided,
        color: dark ? "#e9ecef" : "#495057",
        fontWeight: 500,
      }),
      placeholder: (provided) => ({
        ...provided,
        color: dark ? "rgba(255,255,255,0.5)" : "#6c757d",
      }),
      input: (provided) => ({
        ...provided,
        color: dark ? "#f8f9fa" : "#212529",
      }),
      dropdownIndicator: (provided) => ({
        ...provided,
        display: "none",
      }),
      indicatorSeparator: () => ({
        display: "none",
      }),
      menu: (provided) => ({
        ...provided,
        display: "none",
      }),
    }),
    [dark]
  );

  if (!payment) return <div className="p-4">Loading payment data...</div>;

  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      <div className="row px-2">
        <div className="col-md-8 mb-2">
          <label className="form-label">Member</label>
          <Select
            value={{
              label: `Card ID: ${payment.user_id}, Name: ${getFullName(payment)}, Phone: ${payment.phone || "-"}`,
              value: payment.user_id,
            }}
            isDisabled
            styles={selectStyles}
            className="react-select-member"
            classNamePrefix="react-select"
          />
        </div>
      </div>

      <div className="row px-2">
        <div className="col-md-4 mb-2">
          <label className="form-label">Amount (₹)</label>
          <input
            type="number"
            className="form-control"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </div>
        <div className="col-md-4 mb-2">
          <label className="form-label">Payment Type</label>
          <select
            className="form-select"
            value={paymentType}
            onChange={(e) => setPaymentType(e.target.value)}
            required
          >
            <option value="">-- Select --</option>
            <option value="Cash">Cash</option>
            <option value="UPI">UPI</option>
            <option value="Bank Transfer">Bank Transfer</option>
            <option value="Net Banking">Net Banking</option>
          </select>
        </div>
        <div className="col-md-4 mb-2">
          <label className="form-label">Payment Date</label>
          <input
            type="date"
            className="form-control"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            required
          />
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-4 px-2">
        <button type="submit" className="btn add-user-btn">
          Update Payment
        </button>
      </div>

      <style>{`
        .add-user-btn {
          background-color: #4B2C3B !important;
          color: #fff !important;
          border: none;
          transition: background 0.2s;
        }
        .add-user-btn:hover:not(:disabled) {
          background-color: #D3AF37 !important;
          color: #fff !important;
        }
        .add-user-btn:disabled {
          background-color: #4B2C3B !important;
          color: #fff !important;
          opacity: 0.7;
        }
      `}</style>
    </form>
  );
};

export default EditPaymentModal;
